import { LoaderCircle, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type {
  PlanReviewChecklistResult,
  PlanReviewEvidence,
  PlanReviewVisualItemProgress,
} from "@/api/plan_review";
import {
  collapseDuplicateVisionEvidence,
  evidenceMatchedLabel,
  evidenceSourceLabel,
  formatCorrectionReason,
  humanizeChecklistStatus,
  shortChecklistStatusLabel,
  showsExtractedValue,
  statusPillClass,
} from "@/components/plan_review/planReviewDisplay";

function evidenceCitationTitle(evidence: PlanReviewEvidence, citationNumber: number) {
  if (evidence.source) return evidence.source;
  if (typeof evidence.page === "number") return `Page ${evidence.page}`;
  return `Source ${citationNumber}`;
}

function visualProgressLabel(status: PlanReviewVisualItemProgress["status"]): string {
  switch (status) {
    case "pending":
      return "Looking";
    case "finding":
      return "Finding";
    case "extracting":
    case "extracted":
      return "Extracting";
    case "error":
      return "Look failed";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function hidesVerdict(item: PlanReviewChecklistResult, visualProgress?: PlanReviewVisualItemProgress) {
  return !!visualProgress && item.status === "pending";
}

function PlanReviewEvidenceCitation({
  citationNumber,
  evidence,
  onHighlightOnPlan,
  canViewOnPlan,
  isActive,
}: {
  citationNumber: number;
  evidence: PlanReviewEvidence;
  onHighlightOnPlan: () => void;
  canViewOnPlan: boolean;
  isActive: boolean;
}) {
  const title = evidenceCitationTitle(evidence, citationNumber);
  const matchedLabel = evidenceMatchedLabel(evidence.matched_label);
  const sourceLabel = evidence.source_type === "vision" ? null : evidenceSourceLabel(evidence.source_type);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex h-5 w-5 items-center justify-center rounded-md border text-[11px] font-semibold ${
            isActive
              ? "border-blue-600 bg-blue-600 text-white"
              : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
          }`}
          aria-label={`Highlight plan source ${citationNumber}`}
          aria-pressed={isActive}
          onClick={() => {
            if (canViewOnPlan) onHighlightOnPlan();
          }}
        >
          {citationNumber}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={8}
        data-testid="plan-review-citation-popover"
        className="ordinal-scrollbar w-80 max-h-[min(24rem,var(--radix-popover-content-available-height))] space-y-3 overflow-y-auto overscroll-y-contain p-3"
      >
        <div className="text-xs font-medium text-slate-900">{title}</div>
        {(sourceLabel || matchedLabel) && (
          <div className="flex flex-wrap gap-1.5">
            {sourceLabel && (
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                {sourceLabel}
              </span>
            )}
            {matchedLabel && (
              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                {matchedLabel}
              </span>
            )}
          </div>
        )}
        {!!evidence.excerpt && <p className="text-xs leading-relaxed text-slate-600">{evidence.excerpt}</p>}
      </PopoverContent>
    </Popover>
  );
}

type PlanReviewChecklistResultCardProps = {
  item: PlanReviewChecklistResult;
  viewerPdfUrl: string | null;
  isFocused: boolean;
  activeCitationIndex: number | null;
  onViewOnPlan: (
    item: PlanReviewChecklistResult,
    page?: number | null,
    evidenceIndex?: number | null,
    highlightMode?: "all" | "citation" | "off",
  ) => void;
  onEdit: (item: PlanReviewChecklistResult) => void;
  /** When true, hide human edit (e.g. while plan review is still processing) */
  readOnly?: boolean;
  visualProgress?: PlanReviewVisualItemProgress;
};

export function PlanReviewChecklistResultCard({
  item,
  viewerPdfUrl,
  isFocused,
  activeCitationIndex,
  onViewOnPlan,
  onEdit,
  readOnly = false,
  visualProgress,
}: PlanReviewChecklistResultCardProps) {
  const summaryText = item.explanation || item.reason;
  const evidence = collapseDuplicateVisionEvidence(Array.isArray(item.evidence) ? item.evidence : []);
  const statusLabel = humanizeChecklistStatus(item.status);
  const visualPending = hidesVerdict(item, visualProgress);

  return (
    <div
      className={`rounded-md border p-2.5 space-y-2 ${
        isFocused ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex min-w-0 items-start gap-2">
        <div className="min-w-0 flex-1 text-sm font-medium leading-snug text-slate-900">{item.label}</div>
        {visualPending && visualProgress?.status === "error" ? (
          <span
            className="inline-flex shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-800"
            data-testid="plan-review-visual-progress"
            title={visualProgress.message}
          >
            Look failed
          </span>
        ) : visualPending && visualProgress ? (
          <span
            className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700"
            data-testid="plan-review-visual-progress"
          >
            <LoaderCircle className="h-3 w-3 motion-safe:animate-spin" aria-hidden="true" />
            {visualProgressLabel(visualProgress.status)}
          </span>
        ) : (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${statusPillClass(item.status)}`}
                >
                  {shortChecklistStatusLabel(item.status)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{statusLabel}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {!!summaryText && <p className="text-xs leading-relaxed text-slate-600">{summaryText}</p>}

      {showsExtractedValue(item) && (
        <p className="text-xs text-slate-500">
          Extracted: <span className="text-slate-700">{String(item.extracted_value)}</span>
        </p>
      )}

      {(evidence.length > 0 || item.edited || !readOnly) && (
        <div className="flex items-center justify-between gap-2 pt-0.5">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            {evidence.map((entry, index) => (
              <PlanReviewEvidenceCitation
                key={`${item.key}-citation-${index}`}
                citationNumber={index + 1}
                evidence={entry}
                isActive={isFocused && activeCitationIndex === index}
                canViewOnPlan={!!viewerPdfUrl && typeof entry.page === "number"}
                onHighlightOnPlan={() => onViewOnPlan(item, entry.page ?? null, index, "citation")}
              />
            ))}
            {item.edited && <span className="text-[11px] font-medium text-slate-500">Edited</span>}
          </div>

          {!readOnly && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(item)}
              aria-label={`Edit ${item.label}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {item.correction_reason && (
        <div className="text-[11px] text-slate-500">Correction: {formatCorrectionReason(item.correction_reason)}</div>
      )}
      {item.correction_note && <div className="text-[11px] text-slate-500">Note: {item.correction_note}</div>}
    </div>
  );
}
